import { LitElement, html } from 'lit-element';
import { repeat } from 'lit-html/directives/repeat.js';

import translate from '../../utils/webcomponents/translate';

import * as underlyingOverviewApi from './underlying-overview-api';

class UnderlyingOverview extends LitElement {
	static get properties() {
		return {
			isLoading: { type: Boolean },
			isError: { type: Boolean },
			filters: { type: Array },
			items: { type: Array },
		};
	}

	constructor() {
		super();

		this.isLoading = false;
		this.isError = false;

		this.filters = [];
		this.items = [];
	}

	async load() {
		this.isLoading = true;
		this.isError = false;

		try {
			const { search } = window.location;
			const { alphabeticalBlocks, filters } = await underlyingOverviewApi.get(search);

			this.items = alphabeticalBlocks;
			this.filters = filters;
		} catch (e) {
			this.isError = true;
		} finally {
			this.isLoading = false;
		}
	}

	render() {
		if (this.isError) {
			return this.renderError();
		}

		return this.renderOverview();
	}

	renderOverview() {
		const loader = this.renderLoader();
		const form = this.renderForm();
		const underlyings = this.renderUnderlyings();

		return html`
			<div class="underlying-overview">
				${loader}
				<div class="underlying-overview__form">
					${form}
				</div>
				<div class="underlying-overview__items">
					${underlyings}
				</div>
			</div>
		`;
	}

	renderForm() {
		return html`
			<form @change="${this.onChangeFilters}" @reset="${this.onReset}" class="form">
				<x-underlying-overview-filters .filters="${this.filters}"></x-underlying-overview-filters>
			</form>
		`;
	}

	renderUnderlyings() {
		return repeat(this.items, item => item.id, ({ title, underlyings }) => html`
			<div class="underlying-overview__item">
				<div class="card card--primary">
					<div class="card__header">
						<h2 class="underlying-block-header">${title}</h2>
					</div>
					<x-underlying-overview-table .items="${underlyings}"></x-underlying-overview-table>
				</div>
			</div>
		`);
	}

	renderLoader() {
		return this.isLoading
			? html`<x-loader .isLoading="${this.isLoading}"></x-loader>`
			: '';
	}

	renderError() {
		const errorFeedback = translate('alphabeticalunderlyings', 'somethingWentWrong', 'Something went wrong. Please try again.');
		const labelRetry = translate('alphabeticalunderlyings', 'retry', 'Retry');

		return html`
			<div class="trailer">
				<x-message modifier="error" icon="warning" message="${errorFeedback}"></x-message>
				<button class="button" @click="${this.load}">${labelRetry}</button>
			</div>
		`;
	}

	connectedCallback() {
		super.connectedCallback();

		this.load();
	}

	getQueryString(form) {
		return `?${new URLSearchParams(new FormData(form)).toString()}`;
	}

	setQueryString(queryString) {
		window.history.pushState({}, '', queryString);
	}

	onChangeFilters(e) {
		const queryString = this.getQueryString(e.target.form);

		this.setQueryString(queryString);

		this.load();
	}

	onReset() {
		this.setQueryString(location.pathname);

		this.load();
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-underlying-overview', UnderlyingOverview);

export default UnderlyingOverview;
